/*
  Venue photos: the scroll changes the picture.

  Measured on biontec.ch (.home-scroll3_img-wrapper), which does this with GSAP
  ScrollTrigger. The mechanism needs no library: every photo is a layer pinned
  to the bottom of a fixed-height frame, and the scroll grows that layer's
  height from 0 to 100%. Because the photo inside is anchored to the bottom at
  the frame's full height, the layer growing upwards uncovers it — the photo
  never moves, so there is no drift and no squash.

  The layers stack in source order with rising z-index: the first is always
  fully drawn (it is what the section rests on before any scrolling), and each
  one after it wipes up over the one before.

  Two settings shape it, both read off the element as custom properties:
    --venue-scroll-length   how much scrolling the whole sequence spans, in %
                            of the viewport height
    --venue-smoothing       how far the wipe lags behind the wheel, 0-90%.
                            Their scrub feel, done with a lerp rather than GSAP.
*/
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const frames = [];
  let ticking = false;
  let running = false;

  const clamp = (value) => (value < 0 ? 0 : value > 1 ? 1 : value);

  const numberProp = (element, name, fallback) => {
    const raw = parseFloat(getComputedStyle(element).getPropertyValue(name));
    return Number.isFinite(raw) ? raw : fallback;
  };

  // Starts as the frame reaches the lower part of the screen and runs for the
  // configured share of a viewport height, the same shape as the scrubbed
  // headings in landing-split.js.
  const targetFor = (frame) => {
    const viewport = window.innerHeight;
    const start = viewport * 0.85;
    const end = start - viewport * (frame.length / 100);
    return clamp((start - frame.el.getBoundingClientRect().top) / (start - end));
  };

  const paint = (frame) => {
    const span = 1 / (frame.layers.length - 1);
    frame.layers.forEach((layer, index) => {
      if (index === 0) return;
      const local = clamp((frame.current - (index - 1) * span) / span);
      layer.style.height = `${(local * 100).toFixed(2)}%`;
    });
  };

  const step = () => {
    let moving = false;
    frames.forEach((frame) => {
      const distance = frame.target - frame.current;
      if (Math.abs(distance) < 0.0005) {
        frame.current = frame.target;
      } else {
        frame.current += distance * frame.ease;
        moving = true;
      }
      paint(frame);
    });

    running = moving;
    if (moving) requestAnimationFrame(step);
  };

  const measure = () => {
    frames.forEach((frame) => {
      frame.target = targetFor(frame);
    });
    if (!running) {
      running = true;
      requestAnimationFrame(step);
    }
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      measure();
      ticking = false;
    });
  };

  const init = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-venue]').forEach((el) => {
      if (el.dataset.venueInit === 'true') return;
      el.dataset.venueInit = 'true';

      const layers = Array.prototype.slice.call(el.querySelectorAll('.venue__layer'));
      // One photo has nothing to wipe to; it stays as the section's backdrop.
      if (layers.length < 2) return;

      if (reduced) {
        layers.forEach((layer) => {
          layer.style.height = '100%';
        });
        return;
      }

      const smoothing = numberProp(el, '--venue-smoothing', 60);
      frames.push({
        el,
        layers,
        length: numberProp(el, '--venue-scroll-length', 120),
        current: 0,
        target: 0,
        // 0% smoothing follows the wheel exactly, 90% crawls after it.
        ease: Math.max(0.02, 1 - smoothing / 100),
      });
    });

    if (frames.length && !init.bound) {
      init.bound = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
    }

    if (frames.length) {
      // Land on the right frame straight away rather than wiping in from zero.
      frames.forEach((frame) => {
        frame.target = targetFor(frame);
        frame.current = frame.target;
        paint(frame);
      });
    }
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
